import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import React from 'react';
import { ModalOverlay, PlainButton, useIsMobile } from 'components/v3';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';

// The shell the Columns and Export dialogs share (design sub_modals.jsx
// Modal + Head + .mx-foot): a big centred panel, full screen on phones.

const Head = styled.div`
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: ${(p) => (p.$mobile ? '16px 16px 12px' : '22px 24px 18px')};
`;

const HeadIcon = styled.div`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  box-sizing: border-box;
  background: ${(p) => sx(p.theme).tint};
  border: 1px solid ${(p) => sx(p.theme).tintB};
  color: ${ORANGE};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeadMain = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 1px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${(p) => (p.$mobile ? '19px' : '22px')};
  font-weight: 800;
  letter-spacing: -0.3px;
  line-height: 1.2;
  color: ${(p) => sx(p.theme).text};
`;

const Sub = styled.div`
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.45;
  color: ${(p) => sx(p.theme).label};
`;

const Close = styled(PlainButton)`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).text2};

  &:hover {
    color: ${(p) => sx(p.theme).text};
    background: ${(p) => sx(p.theme).fill};
  }
`;

export const Foot = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: ${(p) =>
    p.$mobile
      ? '12px 16px calc(12px + env(safe-area-inset-bottom))'
      : '14px 24px'};
  border-top: 1px solid ${(p) => sx(p.theme).line};
  background: ${(p) => sx(p.theme).band};
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const FootNote = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => sx(p.theme).label};
`;

export const DialogButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: ${(p) => (p.$sm ? '34px' : '42px')};
  padding: 0 ${(p) => (p.$sm ? '12px' : '18px')};
  border-radius: 10px;
  font-size: ${(p) => (p.$sm ? '13px' : '14px')};
  font-weight: 700;
  white-space: nowrap;
  ${(p) => (p.$grow ? 'flex: 1;' : '')}
  border: 1px solid
    ${(p) => {
    if (p.$danger) return sx(p.theme).error;
    return p.$primary ? ORANGE : sx(p.theme).line2;
  }};
  background: ${(p) => {
    if (p.$danger) return sx(p.theme).error;
    return p.$primary ? ORANGE : 'transparent';
  }};
  color: ${(p) => (p.$primary || p.$danger ? '#FFFFFF' : sx(p.theme).text2)};

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$danger ? sx(p.theme).error : ORANGE)};
    color: ${(p) => (p.$primary || p.$danger ? '#FFFFFF' : sx(p.theme).text)};
  }

  &:disabled {
    opacity: 0.45;
  }
`;

export const LinkButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.$danger ? sx(p.theme).error : sx(p.theme).otext)};

  &:hover {
    background: ${(p) => (p.$danger ? sx(p.theme).errorBg : sx(p.theme).tint)};
  }
`;

export const Label = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).otext};
`;

const DialogShell = ({ label, icon, title, sub, onClose, children }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const x = sx(theme);
  return (
    <ModalOverlay
      open
      onClose={onClose}
      label={label}
      placement={isMobile ? 'full' : 'center'}
      backdrop={x.overlay}
      zIndex={1150}
      panelStyle={
        isMobile
          ? {
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: x.card,
              color: x.text,
            }
          : {
              width: 'min(960px, 100%)',
              height: 'min(800px, 100%)',
              display: 'flex',
              flexDirection: 'column',
              background: x.card,
              color: x.text,
              border: `1px solid ${x.line2}`,
              borderRadius: 20,
              boxShadow: x.pop,
              overflow: 'hidden',
            }
      }
    >
      <Head $mobile={isMobile}>
        <HeadIcon aria-hidden="true">
          <Glyph name={icon} size={20} />
        </HeadIcon>
        <HeadMain>
          <Title $mobile={isMobile}>{title}</Title>
          {sub ? <Sub>{sub}</Sub> : null}
        </HeadMain>
        <Close onClick={onClose} aria-label="Close" data-help="dialog-close">
          <Glyph name="x" size={18} />
        </Close>
      </Head>
      {children}
    </ModalOverlay>
  );
};

DialogShell.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

DialogShell.defaultProps = { sub: null, children: null };

export default DialogShell;
